// fi nav registry (FI-1869): single declarative model of every navigation
// destination and its gating, extracted from the legacy navbar tenant_type x
// role branch matrix (navbar.vue:226-395), the shell rails (report_shell,
// admin_shell, tenant_config_shell, map_shell, booking_shell), and the nav
// utils (map_pages, booking_pages, vsm_access, alerts_access). consumed by
// the horizon nav components (FiTopNav / FiSectionNav) and, later, the
// dashboard card grids. classic surfaces are NOT refactored onto this in v1.
//
// deliberate normalizations vs the legacy code (drift fixes, on purpose):
//   - feature flags are read through isFeatureEnabled() (tbn tenants store
//     string-typed flags; legacy template gates used raw truthiness, so a
//     string 'false' could show an item). exceptions: fleet_assistant_enabled
//     and help_center_enabled keep their default-on `!== false` semantics.
//   - the tenant features map is always guarded (legacy gates like
//     user.features[tenant].alerts_enabled threw when the tenant key missed).
//   - every staff gate uses isEffectiveAdmin (role admin AND not muggle
//     view-as). legacy drifted: the settings gear and the intrastate booking
//     report used raw role checks and survived muggle preview.
//   - labels unified per destination (legacy: 'Map' vs 'Fleet Map', 'VSM' vs
//     'Vehicle Status Manager' for the same route).
//   - the maps tree renders whole for field/driver/customer-care roles who
//     legacy-linked only a standalone vehicle_map; /maps was already
//     deep-linkable by them (no router guard), so this is IA, not access.
//   - future map gains `smart_dispatching_enabled` as a third arm so cm
//     dispatch customers keep it when it leaves the legacy dispatch dropdown.
//
// faithful oddities preserved on purpose (do not "fix" without a ticket):
//   - hardcoded tenant gates, centralized in TENANTS below (dattco driver
//     bid, jeffersonlines misc reports, mtrwestern hours reports, anderson
//     fa settings tab + intrastate report).
//   - fasttrak vsm goes straight to vehicle_status_dvir_fasttrak with no
//     feature check (navbar.vue:232,241 behavior).
//   - tbn managers keep vsm history even with views_reports_enabled
//     (showVsmHistoryNavForManager drift, navbar.vue:722-724).
//   - group message stays dispatch/manager only; the broader
//     hasGroupMessageAccess helper in alerts_access.js was never wired up.
//   - tbn 'parent' tenants (id substring match) shadow every role branch
//     except booking-customer down to fleet map + reports (navbar.vue:306).
//   - router lockdowns for booking-customer and dattco drivers
//     (router/index.js:839-857) are mirrored here so we never render a
//     link the guard would bounce.

import { vsmRouteName, hasVsmHistoryAccess } from '@/utils/vsm_access'
import { isFeatureEnabled } from '@/utils/alerts_access'

// hardcoded tenant ids carried over from legacy gates. candidates for real
// feature flags; keep the list short and loud.
export const TENANTS = {
  DATTCO: 'dattco-413f464d',
  JEFFERSON_LINES: 'jeffersonlines-d637fdad',
  MTR_WESTERN: 'mtrwestern-tbn-3db5da5g7',
  ANDERSON: 'anderson-759g468f',
}

// public legal pages on the company site, linked from both the top-bar help
// menu and the rail's help section
const LEGAL_URLS = {
  terms: 'https://www.fleet-integrations.com/terms.html',
  privacy: 'https://www.fleet-integrations.com/privacy.html',
}

// ---------------------------------------------------------------------------
// context
// ---------------------------------------------------------------------------

// everything a gate is allowed to look at. built once per render from the
// vuex profile state; gates stay pure functions of this object.
export function buildNavContext(user, viewAs) {
  const tenant = (user && user.tenant) || ''
  const features = (user && user.features && user.features[tenant]) || {}
  return {
    user,
    role: (user && user.role) || '',
    tenant,
    tenantType: (user && user.types && user.types[tenant]) || '',
    features,
    viewAs: viewAs || null,
    isEffectiveAdmin: !!user && user.role === 'admin' && viewAs !== 'non-admin',
    telematicsType: (user && user.telematics_types && user.telematics_types[tenant]) || '',
  }
}

// ---------------------------------------------------------------------------
// branch predicates (the legacy v-if/v-else-if chains as named functions)
// ---------------------------------------------------------------------------

const OPS_FIELD_ROLES = ['dispatch', 'garage', 'wash-bay']

function isOpsFieldRole(ctx) {
  return OPS_FIELD_ROLES.includes(ctx.role)
}

export function isTbnParent(ctx) {
  // legacy substring match (navbar.vue:306, report_shell.vue:155)
  return ctx.tenantType === 'TBN' && String(ctx.tenant).indexOf('parent') !== -1
}

// which legacy navbar branch a cm user falls into (order mirrors the
// template's v-if chain, navbar.vue:255-303)
export function cmBranch(ctx) {
  if (isOpsFieldRole(ctx)) return 'ops'
  if (ctx.role === 'booking-customer') return 'booking-customer'
  if (ctx.role === 'driver') return 'driver'
  if (ctx.role === 'customer-care') return 'customer-care'
  if (ctx.role === 'manager') return 'manager'
  return 'default'
}

// tbn chain (navbar.vue:306-391); 'parent' shadows everything but
// booking-customer because it comes first in the template
export function tbnBranch(ctx) {
  if (isTbnParent(ctx) && ctx.role !== 'booking-customer') return 'parent'
  if (ctx.role === 'booking-customer') return 'booking-customer'
  if (isOpsFieldRole(ctx)) return 'ops'
  if (ctx.role === 'driver') return 'driver'
  if (ctx.role === 'manager') return 'manager'
  return 'default'
}

function branchOf(ctx) {
  if (ctx.tenantType === 'CM') return cmBranch(ctx)
  if (ctx.tenantType === 'TBN') return tbnBranch(ctx)
  return null
}

function inDefaultBranch(ctx) {
  return branchOf(ctx) === 'default'
}

export function isDattcoDriver(ctx) {
  return ctx.role === 'driver' && ctx.tenant === TENANTS.DATTCO
}

function isBookingCustomer(ctx) {
  return ctx.role === 'booking-customer'
}

// roles the router guard locks to a route whitelist (router/index.js:839-857)
function isLockedRole(ctx) {
  return isBookingCustomer(ctx) || isDattcoDriver(ctx)
}

// ---------------------------------------------------------------------------
// reports predicates (ported from report_shell.vue:143-184)
// ---------------------------------------------------------------------------

const NO_REPORTS_ROLES = ['dispatch', 'driver', 'booking-customer', 'customer-care', 'wash-bay']

export function hasReportsAccess(ctx) {
  if (ctx.tenantType === 'CM-FLEET') return true
  if (ctx.tenantType === 'FASTTRAK') return false
  if (NO_REPORTS_ROLES.includes(ctx.role)) return false
  if (ctx.isEffectiveAdmin) return true
  if (ctx.role === 'garage') return ctx.tenantType !== 'TBN'
  return isFeatureEnabled(ctx.features.views_reports_enabled)
}

export function reportsRestrictedRole(ctx) {
  if (ctx.isEffectiveAdmin) return null
  if (ctx.tenantType === 'CM-FLEET') return null
  if (ctx.role === 'garage') return 'garage'
  if (isTbnParent(ctx) && ctx.role !== 'customer-admin') return 'tbn-parent'
  if (ctx.role === 'manager') return 'manager'
  return null
}

const isTbn = ctx => ctx.tenantType === 'TBN'
const isCmFleet = ctx => ctx.tenantType === 'CM-FLEET'

function showActualsGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'garage') return false
  if (isCmFleet(ctx)) return false
  if (rr === 'manager' || rr === 'tbn-parent') return true
  return isFeatureEnabled(ctx.features.actuals_enabled)
}

function showPlannedGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'manager' || rr === 'tbn-parent') return false
  if (isCmFleet(ctx)) return false
  return true
}

function showOtpGroup(ctx) {
  if (reportsRestrictedRole(ctx)) return false
  return isFeatureEnabled(ctx.features.views_reports_otp_report_enabled) || isCmFleet(ctx)
}

function showMiscGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'garage' || rr === 'manager') return false
  if (isCmFleet(ctx)) return false
  return true
}

// full (unrestricted) reports audience within a visible group
const fullReports = ctx => !reportsRestrictedRole(ctx)

// ---------------------------------------------------------------------------
// vsm / alerts destinations (dynamic route names)
// ---------------------------------------------------------------------------

function vsmRoute(ctx) {
  // fasttrak legacy hardcode, no feature check (navbar.vue:232,241)
  if (ctx.tenantType === 'FASTTRAK') return 'vehicle_status_dvir_fasttrak'
  return vsmRouteName(ctx.tenantType, ctx.features)
}

// who may see vsm entries at all (role/branch matrix from the legacy navbar)
function vsmRoleGate(ctx) {
  return ctx.tenantType === 'FASTTRAK' ||
    (ctx.tenantType === 'CM' && ['ops', 'manager', 'default'].includes(cmBranch(ctx))) ||
    (ctx.tenantType === 'TBN' && ['ops', 'manager', 'default'].includes(tbnBranch(ctx)))
}

// a secondary vsm mode item shows only when: the role matrix allows vsm, the
// tenant actually has vsm (base flag), the mode's own flag holds, and the
// mode is not already where the primary item routes (no duplicates)
function vsmModeGate(ctx, modeRouteName, modeFlagEnabled) {
  if (!vsmRoleGate(ctx) || ctx.tenantType === 'FASTTRAK') return false
  if (!isFeatureEnabled(ctx.features.vehicle_status_management_enabled)) return false
  if (!modeFlagEnabled) return false
  return vsmRoute(ctx) !== modeRouteName
}

// legacy showVsmHistoryNav (navbar.vue:717-721) + the tbn manager variant
// (722-724) that skips both exclusions; cm managers never had the item
function showVsmHistory(ctx) {
  if (!hasVsmHistoryAccess(ctx.user)) return false
  const b = branchOf(ctx)
  if (ctx.tenantType === 'TBN' && b === 'manager') return true
  if (b !== 'default') return false
  return ctx.role !== 'admin' && !isFeatureEnabled(ctx.features.views_reports_enabled)
}

// ---------------------------------------------------------------------------
// section data
// ---------------------------------------------------------------------------
// section: { key, label, icon, desc?, surfacing: 'top'|'utility'|'contextual',
//            landingRoute?, landingQuery?, landingOnly?, gate(ctx),
//            groups: [{ key, label?, gate?, items }] }
// surfacing 'contextual' = a "you are here" row: rendered in the rail ONLY
// while its page is the active route, so pages owned by the top-bar chrome
// (bell, avatar) still show the selected page in the tree without
// permanently re-listing them. (help was contextual through round 12; round
// 13 promoted it to a real utility section above settings.)
// item: { key, label, icon?, desc?, route|route(ctx), query?|query(ctx),
//         href?(ctx), external?, action?, gate?(ctx), placement?: 'topbar',
//         loose? }
// loose = the row owns its whole multi-tab page: it stays active on any
// query of its route (the tree's disambiguator check is skipped). only for
// routes with a single tree row -- pinned-query siblings need the strict
// match to disambiguate.
// route fns return a route NAME; query fns return a query object; href fns
// return a url string (external links); action items carry a $root event
// name instead of a destination (the rail renders them as buttons).
// omitted gate = inherit group/section.
// desc = the hover-tooltip line; copied from the overview/dashboard pages
// (report_dashboard_shell, admin_tools_dashboard, map_pages, booking_pages,
// tenant_config_shell TAB_OVERVIEW_META) so both surfaces say the same thing.

const SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'fa-th-large',
    desc: 'Every page available to you, as a launcher grid.',
    surfacing: 'top',
    landingRoute: () => 'dashboard',
    landingOnly: true, // the dashboard page is itself a launcher grid
    gate: ctx =>
      ctx.tenantType === 'FASTTRAK' ||
      ctx.tenantType === 'FLEET' ||
      ctx.tenantType === 'CM-FLEET' ||
      inDefaultBranch(ctx),
    groups: [],
  },

  {
    key: 'maps',
    label: 'Maps',
    icon: 'fa-map-marker',
    desc: 'Live, future, and historical vehicle maps.',
    surfacing: 'top',
    landingRoute: ctx => {
      if (isBookingCustomer(ctx)) return 'booking_customer_fleet_map'
      if (isDattcoDriver(ctx)) return 'vehicle_map' // guard whitelist, router/index.js:851-857
      return 'map_dashboard'
    },
    // cm booking-customers have no map anywhere in legacy nav or guard whitelist
    gate: ctx => !(isBookingCustomer(ctx) && ctx.tenantType !== 'TBN'),
    groups: [
      {
        key: 'maps',
        items: [
          { key: 'fleet_map', label: 'Fleet Map', icon: 'fa-map-marker', route: 'map_fleet',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: ctx => !isLockedRole(ctx) },
          { key: 'fleet_map_locked', label: 'Fleet Map', icon: 'fa-map-marker', route: 'vehicle_map',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: isDattcoDriver },
          { key: 'fleet_map_booking_customer', label: 'Fleet Map', icon: 'fa-map-marker',
            route: 'booking_customer_fleet_map',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: ctx => isBookingCustomer(ctx) && ctx.tenantType === 'TBN' },
          { key: 'future_map', label: 'Future Map', icon: 'fa-clock-o', route: 'map_future',
            desc: 'Projected vehicle positions and upcoming dispatch coverage.',
            gate: ctx => !isLockedRole(ctx) &&
              (ctx.isEffectiveAdmin || isTbn(ctx) || isFeatureEnabled(ctx.features.smart_dispatching_enabled)) },
          { key: 'trip_history', label: 'Vehicle Trip History', icon: 'fa-road', route: 'map_trip_history',
            desc: 'Replay a vehicle\'s historical trips, stops, and geofences on the map.',
            gate: ctx => !isLockedRole(ctx) },
          { key: 'route_history', label: 'Vehicle Route History', icon: 'fa-location-arrow',
            route: 'map_route_history',
            desc: 'Review completed vehicle routes for SAMSARA-equipped vehicles.',
            gate: ctx => !isLockedRole(ctx) && (ctx.telematicsType === 'SAMSARA' || isTbn(ctx)) },
          { key: 'booking_map', label: 'Vehicle Booking Map', icon: 'fa-calendar', external: true,
            href: ctx => '/booking-track/' + ctx.tenant + (isTbn(ctx) ? '?type=TBN' : ''),
            desc: 'Map vehicles against their assigned bookings and routes.',
            gate: ctx => !isLockedRole(ctx) },
        ],
      },
    ],
  },

  {
    key: 'bookings',
    label: 'Bookings',
    icon: 'fa-calendar-check-o',
    desc: 'Booking actuals, timelines, and processing history.',
    surfacing: 'top',
    landingRoute: ctx => (isBookingCustomer(ctx) ? 'booking_customer_list_tbn' : 'bookings_dashboard'),
    gate: ctx =>
      isBookingCustomer(ctx) ||
      (ctx.tenantType === 'FASTTRAK' && !isOpsFieldRole(ctx)) ||
      (ctx.tenantType === 'CM' && cmBranch(ctx) === 'default') ||
      (ctx.tenantType === 'TBN' && ['default', 'manager'].includes(tbnBranch(ctx))),
    groups: [
      {
        key: 'booking_customer',
        gate: isBookingCustomer,
        items: [
          { key: 'my_bookings', label: 'My Bookings', route: 'booking_customer_list_tbn',
            desc: 'Your bookings with live tracking and trip details.' },
        ],
      },
      {
        key: 'tbn',
        gate: ctx => isTbn(ctx) && !isBookingCustomer(ctx),
        items: [
          { key: 'actuals_tbn', label: 'Bookings & Actuals', route: 'actuals_tbn',
            desc: 'Review processed booking actuals matched against telematics.' },
          { key: 'driver_actuals_tbn', label: 'Driver Actuals', route: 'driver_actuals_tbn',
            desc: 'Driver booking timeline (HOS on → off) for the selected day.' },
          { key: 'vehicle_actuals_tbn', label: 'Vehicle Actuals', route: 'vehicle_actuals_tbn',
            desc: 'Vehicle booking timeline (location start → finish) for the selected day.' },
          { key: 'troubleshooter_tbn', label: 'Actuals Troubleshooter', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by booking.',
            query: () => ({ source: 'tbn' }) },
          { key: 'stats_tbn', label: 'Actuals Stats', route: 'actuals_stats', query: () => ({ source: 'tbn' }),
            desc: 'Planned vs. actual summary statistics across bookings.' },
          { key: 'update_history', label: 'Booking Update History', route: 'booking_update_history',
            desc: 'History of booking record updates received from TBN over a date range.' },
          { key: 'overlap_updates_tbn', label: 'Overlap Updates', route: 'overlap_updates_history',
            desc: 'History of overlap allocator changes to actuals for troubleshooting.' },
        ],
      },
      {
        key: 'cm',
        gate: ctx => ctx.tenantType === 'CM' && !isBookingCustomer(ctx),
        items: [
          { key: 'actuals_cm', label: 'Bookings & Actuals', route: 'actuals',
            desc: 'Review processed booking actuals matched against telematics.' },
          { key: 'driver_actuals_cm', label: 'Driver Actuals', route: 'driver_actuals',
            desc: 'Driver booking timeline (HOS on → off) for the selected day.' },
          { key: 'vehicle_actuals_cm', label: 'Vehicle Actuals', route: 'vehicle_actuals',
            desc: 'Vehicle booking timeline (location start → finish) for the selected day.' },
          { key: 'troubleshooter_cm', label: 'Actuals Troubleshooter', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by movement.',
            query: () => ({ source: 'cm' }) },
          { key: 'stats_cm', label: 'Actuals Stats', route: 'actuals_stats', query: () => ({ source: 'cm' }),
            desc: 'Planned vs. actual summary statistics across movements.' },
          { key: 'file_history', label: 'Booking File History', route: 'booking_file_list',
            desc: 'History of booking file updates, filterable by date range or type.' },
          { key: 'overlap_updates_cm', label: 'Overlap Updates', route: 'overlap_updates_history',
            desc: 'History of overlap allocator changes to actuals for troubleshooting.' },
        ],
      },
      {
        key: 'fasttrak',
        gate: ctx => ctx.tenantType === 'FASTTRAK',
        items: [
          { key: 'actuals_ft', label: 'Bookings & Actuals', route: 'actuals_fasttrak',
            desc: 'Reservations matched to telematics after the scheduled end time.' },
          { key: 'troubleshooter_ft', label: 'Actuals Troubleshooter', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by reservation.',
            query: () => ({ source: 'fasttrak' }) },
        ],
      },
    ],
  },

  {
    key: 'reports',
    label: 'Reports',
    icon: 'fa-bar-chart',
    desc: 'Actuals, planned, OTP, and misc reporting.',
    surfacing: 'top',
    landingRoute: () => 'report_dashboard',
    // audiences whose legacy nav linked reports; report_shell itself upsells
    // when hasReportsAccess is false, so the section gate is the link gate
    gate: ctx =>
      isCmFleet(ctx) ||
      (ctx.tenantType === 'CM' && ['manager', 'default'].includes(cmBranch(ctx))) ||
      (ctx.tenantType === 'TBN' && ['parent', 'manager', 'default'].includes(tbnBranch(ctx))),
    groups: [
      {
        key: 'actuals',
        label: 'Actuals',
        gate: showActualsGroup,
        items: [
          // tbn list (report_shell.vue:187-209)
          { key: 'otp_daily_tbn', label: 'Daily OTP Report', route: 'actuals_perf_report_tbn',
            desc: 'Compare planned booking times against actual on-time performance by day.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'otp_multiday_tbn', label: 'Multiday OTP Report', route: 'actuals_multiday_otp_report_tbn',
            desc: 'On-time performance trends across a custom date range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'pickup_dropoff_tbn', label: 'Pickup/Dropoff Report', route: 'actuals_billing_report_tbn',
            desc: 'Pickup and dropoff event details per booking.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'start_events_tbn', label: 'Start Events Report', route: 'actuals_start_events_report_tbn',
            desc: 'Vehicle start events matched to scheduled bookings.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'driver_daily_tbn', label: 'Driver Daily Report', route: 'actuals_driver_payroll_report_tbn',
            desc: 'Driver activity and hours for a single day.',
            gate: isTbn }, // also the restricted manager/tbn-parent list
          { key: 'driver_multiday_tbn', label: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn',
            desc: 'Driver activity and hours across multiple days.',
            gate: isTbn },
          { key: 'payroll_summary_tbn', label: 'Payroll Summary Report', route: 'actuals_payroll_summary_report_tbn',
            desc: 'Aggregated payroll totals by driver for a date range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          // cm list (report_shell.vue:194-219)
          { key: 'otp_daily_cm', label: 'Daily OTP Report', route: 'actuals_perf_report',
            desc: 'Compare planned booking times against actual on-time performance by day.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'otp_multiday_cm', label: 'Multiday OTP Report', route: 'actuals_multiday_otp_report',
            desc: 'On-time performance trends across a custom date range.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'mileage_by_booking', label: 'Mileage By Booking', route: 'actuals_mileage_by_booking_report',
            desc: 'Per-booking mileage breakdown for accurate client billing.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'billing_cm', label: 'Billing Report', route: 'actuals_billing_report',
            desc: 'Summarised billing data based on completed actuals.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'vehicle_start_stop', label: 'Vehicle Start/Stop', route: 'actuals_vehicle_report',
            desc: 'Vehicle engine start and stop events per booking.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'driver_daily_cm', label: 'Driver Daily Report', route: 'actuals_driver_payroll_report',
            desc: 'Driver activity and hours for a single day.',
            gate: ctx => !isTbn(ctx) },
          { key: 'driver_multiday_cm', label: 'Driver Multiday Report', route: 'actuals_driver_multiday_report',
            desc: 'Driver activity and hours across multiple days.',
            gate: ctx => !isTbn(ctx) },
          { key: 'payroll_summary_cm', label: 'Payroll Summary Report', route: 'actuals_payroll_summary_report',
            desc: 'Aggregated payroll totals by driver for a date range.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
        ],
      },
      {
        key: 'planned',
        label: 'Planned',
        gate: showPlannedGroup,
        items: [
          // tbn list (report_shell.vue:226-239)
          { key: 'driver_util_daily_tbn', label: 'Driver Utilization Daily', route: 'planned_drivers_daily_tbn', gate: isTbn,
            desc: 'Daily view of driver utilisation against planned bookings.' },
          { key: 'driver_util_weekly_tbn', label: 'Driver Utilization Weekly', route: 'planned_drivers_weekly_util_tbn', gate: isTbn,
            desc: 'Weekly driver utilisation across the fleet.' },
          { key: 'driver_util_monthly_tbn', label: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_tbn', gate: isTbn,
            desc: 'Monthly breakdown of driver type utilisation.' },
          { key: 'driver_dispatch_weekly_tbn', label: 'Driver Dispatch Weekly', route: 'planned_drivers_weekly_dispatch_tbn', gate: isTbn,
            desc: 'Weekly driver dispatch schedule.' },
          { key: 'vehicle_util_daily_tbn', label: 'Vehicle Utilization Daily', route: 'planned_vehicle_daily_tbn', gate: isTbn,
            desc: 'Daily vehicle usage compared to planned schedule.' },
          { key: 'vehicle_util_weekly_tbn', label: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly_tbn', gate: isTbn,
            desc: 'Weekly view of vehicle fleet utilisation.' },
          { key: 'vehicle_util_monthly_tbn', label: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly_tbn', gate: isTbn,
            desc: 'Monthly fleet utilisation by vehicle type.' },
          { key: 'departure_board_tbn', label: 'Vehicle Departure Board', route: 'trip_sheet_tbn', gate: isTbn,
            desc: 'Scheduled departure times and vehicle assignments.' },
          { key: 'dispatch_board_tbn', label: 'Booking Dispatch Board', route: 'dispatch_board_tbn', gate: isTbn,
            desc: 'Live dispatch board for booking management.' },
          { key: 'booking_monthly_tbn', label: 'Booking Monthly View', route: 'booking_monthly_view_tbn', gate: isTbn,
            desc: 'Monthly calendar view of all bookings.' },
          // cm list (report_shell.vue:223-248); vehicle weekly is also the
          // garage-only item, so it skips the garage exclusion
          { key: 'driver_board', label: 'Driver Board', route: 'driver_board',
            desc: 'Real-time driver assignments and availability board.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'driver_util_daily_cm', label: 'Driver Utilization Daily', route: 'planned_drivers_daily',
            desc: 'Daily view of driver utilisation against planned bookings.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'driver_util_monthly_cm', label: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_cm',
            desc: 'Monthly breakdown of driver type utilisation.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'vehicle_util_daily_cm', label: 'Vehicle Utilization Daily', route: 'planned_vehicle_daily',
            desc: 'Daily vehicle usage compared to planned schedule.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'vehicle_util_weekly_cm', label: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly',
            desc: 'Weekly view of vehicle fleet utilisation.',
            gate: ctx => !isTbn(ctx) },
          { key: 'vehicle_util_monthly_cm', label: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly',
            desc: 'Monthly fleet utilisation by vehicle type.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'departure_board_cm', label: 'Vehicle Departure Board', route: 'trip_sheet',
            desc: 'Scheduled departure times and vehicle assignments.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
        ],
      },
      {
        key: 'otp',
        label: 'OTP',
        gate: showOtpGroup,
        items: [
          { key: 'route_history_report', label: 'Route History', route: 'vehicle_route_history',
            desc: 'Historical route data for each vehicle.' },
          { key: 'route_assignments', label: 'Route Assignments', route: 'route_assignments',
            desc: 'Route-to-vehicle assignment history.',
            gate: ctx => !isTbn(ctx) },
          { key: 'route_addresses', label: 'Route Addresses', route: 'route_addresses',
            desc: 'Address list for configured routes.' },
          { key: 'otp_nightly', label: 'OTP Nightly Report History', route: 'report_otp',
            desc: 'Nightly OTP report run history and results.' },
          { key: 'otp_results', label: 'OTP Results History', route: 'otp_history',
            desc: 'Historical OTP results with trends over time.' },
        ],
      },
      {
        key: 'misc',
        label: 'Misc',
        gate: showMiscGroup,
        items: [
          // jefferson lines items unshift to the top of legacy misc
          { key: 'bus_tracker', label: 'Bus Tracker', route: 'bus_tracker',
            desc: 'Live bus tracking view for Jefferson Lines.',
            gate: ctx => ctx.tenant === TENANTS.JEFFERSON_LINES },
          { key: 'arrivals_departures', label: 'Arrive/Depart Dashboard', route: 'arrivals_departures_board',
            desc: 'Arrivals and departures live dashboard.',
            gate: ctx => ctx.tenant === TENANTS.JEFFERSON_LINES },
          { key: 'unplanned_vehicle', label: 'Unplanned Vehicle Activity', route: 'report_unplanned_vehicle',
            desc: 'Vehicles that moved outside of scheduled bookings.',
            gate: ctx => !isTbn(ctx) },
          { key: 'unplanned_driver', label: 'Unplanned Driver Activity', route: 'report_unplanned_hos',
            desc: 'Driver activity recorded outside planned bookings.',
            gate: ctx => !isTbn(ctx) },
          // the one misc item tbn-parent keeps (report_shell.vue:263-265)
          { key: 'event_notes', label: 'Event Notes History', route: 'event_notes_list',
            desc: 'Notes and annotations recorded against fleet events.' },
          { key: 'hos_history', label: 'HOS Event History', route: 'hos_history_report',
            desc: 'Hours of service events for equipped vehicles.',
            gate: ctx => fullReports(ctx) && (isTbn(ctx) || ctx.telematicsType === 'SAMSARA') },
          { key: 'tyler_routes', label: 'Tyler Route Processor', route: 'tyler_route_processor',
            desc: 'Process and sync Tyler route data.',
            gate: ctx => fullReports(ctx) && isTbn(ctx) && isFeatureEnabled(ctx.features.tbn_tyler_route_sync_enabled) },
          // note: escapes the reports shell today (top-level route)
          { key: 'vsm_history_report', label: 'VSM History', route: 'vehicle_status_history',
            desc: 'Historical vehicle status and inspection records.',
            gate: ctx => fullReports(ctx) && isFeatureEnabled(ctx.features.vehicle_status_management_enabled) },
          { key: 'report_converter', label: 'Report Converter', route: 'report_converter',
            desc: 'Convert report formats for third-party system compatibility.',
            gate: ctx => fullReports(ctx) && isFeatureEnabled(ctx.features.views_reports_converter_enabled) },
          { key: 'maint_summary', label: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary',
            desc: 'Summary of vehicle maintenance activities and costs.',
            gate: ctx => fullReports(ctx) && !isTbn(ctx) && isFeatureEnabled(ctx.features.vehicle_status_management_maint_summary_enabled) },
          { key: 'hours_by_client', label: 'Hours By Client Report', route: 'actuals_hours_by_client_report_tbn',
            desc: 'Total hours worked grouped by client.',
            gate: ctx => fullReports(ctx) && ctx.tenant === TENANTS.MTR_WESTERN },
          { key: 'hours_by_booking', label: 'Hours By Booking Report', route: 'actuals_hours_by_booking_report_tbn',
            desc: 'Hours breakdown per individual booking.',
            gate: ctx => fullReports(ctx) && ctx.tenant === TENANTS.MTR_WESTERN },
          // legacy used raw role==='admin' here; normalized to isEffectiveAdmin
          { key: 'intrastate', label: 'Intrastate Booking Report', route: 'intrastate_booking_report_tbn',
            desc: 'Report on intrastate travel bookings.',
            gate: ctx => fullReports(ctx) && isTbn(ctx) && (ctx.tenant === TENANTS.ANDERSON || ctx.isEffectiveAdmin) },
        ],
      },
    ],
  },

  {
    key: 'fleet',
    label: 'Fleet',
    icon: 'fa-bus',
    desc: 'Vehicle status, alerts, dispatch, and driver tools.',
    surfacing: 'top',
    // landing resolves to the first visible item (no fixed home page)
    gate: () => true, // visibility falls out of item gates; empty section drops
    groups: [
      {
        key: 'status',
        items: [
          // develop's alerts refactor moved /alerts to a shell whose default
          // child is alerts_dashboard (navbar links here too) and dropped the
          // computed alertsRouteName. the FI-1869 merge left registry importing
          // that removed helper, so alertsRoute() called undefined -> resolveNav
          // threw and the whole horizon rail vanished. link the shell directly.
          { key: 'alerts', label: 'Alerts', icon: 'fa-bell-o', route: 'alerts_dashboard',
            desc: 'Triggered fleet alerts and their history.',
            gate: ctx => ctx.tenantType === 'FASTTRAK' ? !isOpsFieldRole(ctx) : inDefaultBranch(ctx) },
          { key: 'vsm', label: 'Vehicle Status Manager', icon: 'fa-wrench', route: vsmRoute,
            desc: 'Track vehicle status, inspections, and maintenance.',
            gate: vsmRoleGate },
          // the vsm has distinct mode pages (status board / dvir / maintenance);
          // the primary item above routes to the tenant's best mode
          // (vsmRouteName priority), these expose the OTHER enabled modes.
          // fasttrak stays on its single hardcoded dvir page.
          { key: 'vsm_board', label: 'VSM: Status Board',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_tbn' : 'vehicle_status'),
            desc: 'The status board view of the Vehicle Status Manager.',
            gate: ctx => vsmModeGate(ctx, ctx.tenantType === 'TBN' ? 'vehicle_status_tbn' : 'vehicle_status', true) },
          { key: 'vsm_dvir', label: 'VSM: DVIR',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_dvir_tbn' : 'vehicle_status_dvir'),
            desc: 'Driver vehicle inspection reports (DVIR).',
            gate: ctx => vsmModeGate(ctx, ctx.tenantType === 'TBN' ? 'vehicle_status_dvir_tbn' : 'vehicle_status_dvir',
              isFeatureEnabled(ctx.features.vehicle_status_management_dvir_enabled)) },
          { key: 'vsm_maint', label: 'VSM: Maintenance',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_maint_tbn' : 'vehicle_status_maint'),
            desc: 'Vehicle maintenance tracking and history.',
            gate: ctx => vsmModeGate(ctx, ctx.tenantType === 'TBN' ? 'vehicle_status_maint_tbn' : 'vehicle_status_maint',
              isFeatureEnabled(ctx.features.vehicle_status_management_maint_enabled)) },
          { key: 'vsm_history', label: 'VSM History', route: 'vehicle_status_history',
            desc: 'Historical vehicle status and inspection records.',
            gate: showVsmHistory },
          // the sos page (send a request + history) is an ordinary rail page;
          // the top-bar red pill is a separate ALARM item that fi_top_nav only
          // renders while an sos request is actually open (it polls history --
          // there is no active-sos store; legacy showed a permanent nav link)
          { key: 'sos', label: 'Bus SOS', icon: 'fa-exclamation-triangle', route: 'sos_list',
            desc: 'Send a Bus SOS request and review past requests.',
            gate: ctx => isFeatureEnabled(ctx.features.bus_sos_enabled) && !isBookingCustomer(ctx) && !isDattcoDriver(ctx) },
          { key: 'sos_alarm', label: 'SOS', icon: 'fa-exclamation-triangle', route: 'sos_list', placement: 'topbar',
            gate: ctx => isFeatureEnabled(ctx.features.bus_sos_enabled) && !isBookingCustomer(ctx) && !isDattcoDriver(ctx) },
          // cm drivers' only board (navbar.vue:267)
          { key: 'departure_board_standalone', label: 'Vehicle Departure Board', route: 'trip_sheet_standalone',
            desc: 'Scheduled departure times and vehicle assignments.',
            gate: ctx => ctx.tenantType === 'CM' && ctx.role === 'driver' },
        ],
      },
      {
        key: 'communicate',
        items: [
          { key: 'group_message', label: 'Group Message Sender', icon: 'fa-comments-o',
            desc: 'Send a group text message to drivers.',
            route: ctx => (isTbn(ctx) ? 'group_message_sender_tbn' : 'group_message_sender'),
            gate: ctx =>
              (ctx.tenantType === 'CM' || ctx.tenantType === 'TBN') &&
              !isTbnParent(ctx) &&
              (ctx.role === 'dispatch' || ctx.role === 'manager') &&
              isFeatureEnabled(ctx.features.alerts_enabled) },
        ],
      },
      {
        key: 'dispatch',
        label: 'Dispatch',
        // legacy dropdown lives in the cm default branch (navbar.vue:297)
        // and the tbn ops + default branches (320, 367); cm ops never had it
        gate: ctx =>
          isFeatureEnabled(ctx.features.smart_dispatching_enabled) &&
          ((ctx.tenantType === 'CM' && cmBranch(ctx) === 'default') ||
            (ctx.tenantType === 'TBN' && ['ops', 'default'].includes(tbnBranch(ctx)))),
        items: [
          { key: 'smart_drivers', label: 'Smart Driver Mgmt',
            desc: 'Manage the driver list used for smart dispatching.',
            route: ctx => (isTbn(ctx) ? 'driver_list_tbn' : 'driver_list') },
          { key: 'smart_routes', label: 'Smart Route Assigner',
            desc: 'Smart route-to-driver assignment suggestions.',
            route: ctx => (isTbn(ctx) ? 'smart_route_assignments_tbn' : 'smart_route_assignments') },
          { key: 'trip_linker', label: 'Smart Trip Linker', route: 'trip_linker_report_tbn', gate: isTbn,
            desc: 'Link related trips for smarter dispatching.' },
          // faithful gate incl. its default-on explicit-false pattern
          // (navbar.vue:369); reachable audience is tbn default branch only
          { key: 'fleet_assistant', label: 'Fleet Assistant', route: 'fleet_assistant',
            desc: 'Ask the Fleet Assistant about your fleet\'s data.',
            gate: ctx => isTbn(ctx) && inDefaultBranch(ctx) &&
              (ctx.isEffectiveAdmin ||
                (ctx.features.fleet_assistant_enabled !== false &&
                  (['customer', 'customer-admin', 'tbn-admin'].includes(ctx.role) || !!ctx.user.fleet_assistant_access))) },
        ],
      },
      {
        key: 'scorecards',
        label: 'Scorecards',
        gate: ctx => isTbn(ctx) && inDefaultBranch(ctx) && isFeatureEnabled(ctx.features.driver_scorecard_enabled),
        items: [
          { key: 'driver_scorecard', label: 'Driver Scorecard', route: 'driver_scorecard',
            desc: 'Driver performance scorecards.' },
          { key: 'scorecard_comparison', label: 'Driver Scorecard Comparison', route: 'driver_scorecard_comparison',
            desc: 'Compare scorecards across drivers.' },
        ],
      },
      {
        key: 'sync',
        label: 'Sync',
        gate: ctx => isTbn(ctx) && inDefaultBranch(ctx) && isFeatureEnabled(ctx.features.data_services_compare_drivers_enabled),
        items: [
          { key: 'compare_lists', label: 'Compare Driver Lists', route: 'compare_drivers',
            desc: 'Compare driver lists between connected systems.' },
          { key: 'compare_profiles', label: 'Compare Driver Profiles', route: 'compare_driver_profiles',
            desc: 'Compare driver profile details between connected systems.' },
          { key: 'compare_history', label: 'Compare Driver History', route: 'compare_drivers_history',
            desc: 'Past driver comparison runs.' },
        ],
      },
      {
        key: 'driver_bid',
        label: 'Driver Bid',
        gate: ctx => ctx.tenant === TENANTS.DATTCO,
        items: [
          { key: 'bid_board', label: 'Driver Bid Board', route: 'driver_bid_board_tbn',
            desc: 'View and bid on open driver work.',
            gate: ctx => ctx.role === 'driver' },
          { key: 'bid_manage', label: 'Driver Bid Management', route: 'driver_bid_manage_board_tbn',
            desc: 'Manage driver bid postings and awards.',
            gate: ctx => ctx.isEffectiveAdmin || ctx.role === 'customer-admin' },
        ],
      },
    ],
  },

  {
    // FI-1929: erm customer rollout. staff reach the erm from the admin tools
    // preview group (isEffectiveAdmin-gated), so this customer-facing rail
    // destination excludes effective-admins to avoid a duplicate entry.
    // non-staff need the tenant flag plus an allowed role -- mirrors the
    // classic navbar gate and the router guard for ops_desk. muggle-view
    // admins keep role 'admin' (isEffectiveAdmin false), so they still preview
    // this customer destination when the muggled tenant has the flag.
    key: 'erm',
    // abbreviated in the top nav; the hover tooltip's desc and the in-page rail
    // header carry the full name. the staff preview group keeps the full label.
    label: 'ERM',
    icon: 'fa-clipboard',
    desc: 'Log and work incidents, kudos, and notes with distribution and the Huddle recap.',
    surfacing: 'top',
    landingRoute: () => 'ops_desk',
    landingQuery: ctx => ({ tenant: ctx.tenant }),
    landingOnly: true,
    gate: ctx => !ctx.isEffectiveAdmin &&
      (ctx.role === 'admin' || ctx.role === 'customer-admin') &&
      isFeatureEnabled(ctx.features.ops_desk_enabled),
    groups: [],
  },

  {
    // chaz round 13: help gets a real home in the tree, above settings. it
    // also carries the fleet assistant LAUNCH row, so phones -- which lost
    // the floating pill to the bar button and never had the desktop help
    // menu -- reach the help center, the legal pages, and the assistant
    // from the drawer. the top-bar help menu keeps its tab-level shortcuts
    // on desktop.
    key: 'help',
    label: 'Help',
    icon: 'fa-question-circle',
    desc: 'Help center, legal pages, and the Fleet Assistant.',
    surfacing: 'utility',
    gate: () => true,
    groups: [
      {
        // per-tab rows (round 17, chaz): the help center's tabs are
        // first-class rail destinations under horizon, and the in-page tab
        // strip hides there. help_center.vue pins ?tab= as the url source
        // of truth under horizon (incl. articles, whose classic clean-url
        // delete is horizon-suppressed) so these pinned queries match
        // strictly. same keys/gates as HELP_MENU_GROUPS below.
        key: 'help_center',
        // default-on explicit-false pattern (navbar.vue:18-22). locked
        // roles are excluded: their router whitelists bounce /help_center
        gate: ctx => !isLockedRole(ctx) &&
          (ctx.isEffectiveAdmin || ctx.features.help_center_enabled !== false),
        items: [
          { key: 'help_articles', label: 'Help Articles', route: 'help_center',
            query: () => ({ tab: 'articles' }),
            desc: 'How-to articles for every feature.' },
          // non-admins additionally need the company-docs flag
          // (help_center.vue:2877-2881)
          { key: 'company_docs', label: 'Company Docs', route: 'help_center',
            query: () => ({ tab: 'company' }),
            desc: 'Documents your company shares with portal users.',
            gate: ctx => ctx.isEffectiveAdmin || ctx.features.company_documents_in_help_center !== false },
          { key: 'support_tickets', label: 'Support Tickets', route: 'help_center',
            query: () => ({ tab: 'support' }),
            desc: 'Open and track support requests.' },
        ],
      },
      {
        key: 'help_admin',
        label: 'Admin',
        gate: ctx => ctx.isEffectiveAdmin,
        items: [
          { key: 'feedback_review', label: 'Feedback Review', route: 'help_center',
            query: () => ({ tab: 'feedback' }),
            desc: 'Review feedback left on assistant answers.' },
          { key: 'assistant_insights', label: 'Assistant Insights', route: 'help_center',
            query: () => ({ tab: 'insights' }),
            desc: 'Fleet Assistant usage and cost across tenants.' },
          { key: 'help_announcements', label: 'Announcements', route: 'help_center',
            query: () => ({ tab: 'announcements' }),
            desc: 'Compose and publish product announcements.' },
        ],
      },
      {
        key: 'help',
        items: [
          // an action, not a route: opens the assistant panel in place (the
          // full-page fa keeps its own item under fleet > dispatch). same
          // gate as the launcher pill / bar button, minus the route check --
          // rows must not vanish from the tree as the user navigates.
          { key: 'fleet_assistant_launch', label: 'Fleet Assistant', action: 'open-fleet-assistant',
            desc: 'Ask the Fleet Assistant about your fleet\'s data.',
            gate: ctx => faLauncherAvailable(ctx.user, null) },
          { key: 'terms', label: 'Terms of Service', external: true, href: () => LEGAL_URLS.terms },
          { key: 'privacy', label: 'Privacy Policy', external: true, href: () => LEGAL_URLS.privacy },
        ],
      },
    ],
  },

  {
    key: 'settings',
    label: 'Settings',
    icon: 'fa-cog',
    desc: 'Account configuration and user management.',
    surfacing: 'utility',
    // items address the shell's v-show tabs via ?tab= (tenant_config_shell
    // watches the query under horizon) so its unsaved-changes guard keeps
    // working. the flat tenant_config_* routes bypass that guard -- never
    // link those from the rail. the landing is the shell's horizon-only
    // overview pseudo-tab (a card grid of the sections), so /settings no
    // longer drops users straight into General.
    landingRoute: () => 'settings',
    landingQuery: () => ({ tab: 'tab_overview' }),
    // legacy gear used raw role and survived muggle preview; normalized
    gate: ctx => ctx.isEffectiveAdmin || ctx.role === 'customer-admin' || ctx.role === 'tbn-admin',
    groups: [
      {
        key: 'tabs',
        items: [
          { key: 'general', label: 'General', route: 'settings', query: () => ({ tab: 'tab_general' }),
            desc: 'Company profile, branding, and core account details.' },
          { key: 'bookings_actuals', label: 'Bookings & Actuals', route: 'settings', query: () => ({ tab: 'tab_bookings_actuals' }),
            desc: 'How bookings import and match against recorded actuals.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.actuals_enabled) },
          { key: 'map_settings', label: 'Map Settings', route: 'settings', query: () => ({ tab: 'tab_cust_map_views' }),
            desc: 'Default map views, labels, and visibility options.' },
          { key: 'views_reports', label: 'Views & Reports', route: 'settings', query: () => ({ tab: 'tab_views_reports' }),
            desc: 'Which report pages are available and how they behave.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.views_reports_enabled) },
          { key: 'alerts_settings', label: 'Alerts', route: 'settings', query: () => ({ tab: 'tab_alerts' }),
            desc: 'Alert rules and thresholds for fleet events.',
            gate: ctx => ctx.isEffectiveAdmin ||
              isFeatureEnabled(ctx.features.vehicle_status_management_enabled) ||
              isFeatureEnabled(ctx.features.alerts_enabled) },
          { key: 'notifications_settings', label: 'Notifications', route: 'settings', query: () => ({ tab: 'tab_notifications' }),
            desc: 'Notification templates and recipient settings.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.alerts_enabled) },
          { key: 'vsm_settings', label: 'Vehicle Status Mgr', route: 'settings', query: () => ({ tab: 'tab_veh_status_mgmt' }),
            desc: 'Status board, inspection, and maintenance options.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.vehicle_status_management_enabled) },
          { key: 'bus_sos_settings', label: 'Bus SOS Settings', route: 'settings', query: () => ({ tab: 'tab_bus_sos' }),
            desc: 'Emergency SOS recipients, range, and vehicle coverage.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.bus_sos_enabled) },
          { key: 'data_services', label: 'Data Services', route: 'settings', query: () => ({ tab: 'tab_data_services' }),
            desc: 'Data sync services for drivers and vehicles.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.data_services_enabled) },
          // hardcoded anderson customer-admin grant (tenant_config_shell.vue:253-255)
          { key: 'fa_settings', label: 'Fleet Assistant', route: 'settings', query: () => ({ tab: 'tab_fleet_assistant' }),
            desc: 'Fleet Assistant access and behavior for this account.',
            gate: ctx => ctx.isEffectiveAdmin || (ctx.tenant === TENANTS.ANDERSON && ctx.role === 'customer-admin') },
          { key: 'history', label: 'History', route: 'settings', query: () => ({ tab: 'tab_history' }),
            desc: 'A log of settings changes on this account.' },
          { key: 'manage_users', label: 'Manage Users', route: 'settings', query: () => ({ tab: 'tab_user_list' }),
            desc: 'Add users, set roles, and manage access.' },
        ],
      },
    ],
  },

  {
    key: 'admin',
    label: 'Admin Tools',
    icon: 'fa-wrench',
    desc: 'Staff dashboards and system tools.',
    surfacing: 'utility',
    landingRoute: () => 'admin_tools_dashboard',
    gate: ctx => ctx.isEffectiveAdmin,
    groups: [
      {
        key: 'tools',
        items: [
          { key: 'summary', label: 'Summary Dashboard', route: 'admin_dashboard',
            desc: 'High-level overview of system health, tenant activity, and key metrics.' },
          { key: 'tenants', label: 'Account & Billing Stats', route: 'tenant_manager',
            desc: 'Tenant account details, subscription status, and billing statistics.' },
          { key: 'api_usage', label: 'API Usage', route: 'api_usage_dashboard',
            desc: 'API request volumes, response times, and usage breakdowns by tenant.' },
          { key: 'processes', label: 'Process Manager', route: 'process_manager_list',
            desc: 'View and manage background processing jobs across all tenants.' },
          { key: 'simulator', label: 'Actuals Simulator', route: 'actuals_simulator',
            desc: 'Simulate actuals processing to test and debug booking matching logic.' },
          { key: 'shortener', label: 'URL Shortener', route: 'url_shortener',
            desc: 'Create and manage short URLs for sharing fleet integration links.' },
          { key: 'cache', label: 'Cache Manager', route: 'cache_manager',
            desc: 'Inspect and flush cached data for tenants and system resources.' },
        ],
      },
      {
        key: 'preview',
        label: 'Preview',
        items: [
          { key: 'today_board', label: 'Today Board', route: 'ops_board',
            desc: 'Exception-first dispatch board for today\'s departures, active trips, and returns.' },
          { key: 'erm', label: 'Event Response Manager', route: 'ops_desk',
            desc: 'Log and work incidents, kudos, and notes with distribution and the Huddle recap.',
            query: ctx => ({ tenant: ctx.tenant }) },
          { key: 'vsm_board', label: 'Vehicle Status Manager', route: 'vsm_board',
            desc: 'Redesigned vehicle status board. The classic version stays in the main navigation.',
            query: ctx => ({ tenant: ctx.tenant }) },
          { key: 'departure_board_preview', label: 'Departure Board', route: 'departure_board',
            desc: 'Redesigned departure board with display modes suited to wall screens.',
            query: ctx => ({ tenant: ctx.tenant }) },
        ],
      },
    ],
  },

  // contextual rows for the top-bar chrome destinations (see the surfacing
  // note above). the profile landing carries the viewer's own username so
  // an admin opening ANOTHER user from manage users does not light this up
  // (fi_section_nav falls back to the settings path match there). the help
  // center's contextual row retired in round 13: the help section above
  // lists it permanently now.
  {
    key: 'notifications',
    label: 'Notifications',
    icon: 'fa-bell-o',
    desc: 'Your notification history.',
    surfacing: 'contextual',
    landingRoute: () => 'notifications_list',
    landingOnly: true,
    gate: () => true,
    groups: [],
  },
  {
    key: 'profile',
    label: 'Manage Profile',
    icon: 'fa-user-o',
    desc: 'Your account details and preferences.',
    surfacing: 'contextual',
    landingRoute: () => 'user_detail',
    landingQuery: ctx => ({ username: (ctx.user && ctx.user.username) || '' }),
    landingOnly: true,
    gate: () => true,
    groups: [],
  },

]

// utility destinations that live in the top bar rather than the section tree.
// theme picker, view-as, profile, and logout are component behaviors, not nav
// destinations, so they are wired in FiTopNav directly.
export const UTILITY_ITEMS = [
  { key: 'notifications', label: 'Notifications', icon: 'fa-bell-o', route: 'notifications_list',
    placement: 'topbar', gate: () => true },
]

// the top-bar help icon (classic parity position, navbar.vue:17-22) opens a
// compact menu instead of a sidebar section: the help center's top-level tabs
// (help_center.vue:3061-3068) plus the public legal pages on the company
// site. the icon itself always renders -- terms/privacy must stay reachable
// even when a tenant's help center is disabled -- so the feature gate lives
// on the help-center group here, not on the icon.
export const HELP_MENU_GROUPS = [
  {
    key: 'help_center',
    // default-on explicit-false pattern (navbar.vue:18-22)
    gate: ctx => ctx.isEffectiveAdmin || ctx.features.help_center_enabled !== false,
    items: [
      { key: 'help_articles', label: 'Help Articles', icon: 'fa-book', route: 'help_center',
        query: () => ({ tab: 'articles' }) },
      // non-admins additionally need the company-docs flag (help_center.vue:2877-2881)
      { key: 'company_docs', label: 'Company Docs', icon: 'fa-file-text-o', route: 'help_center',
        query: () => ({ tab: 'company' }),
        gate: ctx => ctx.isEffectiveAdmin || ctx.features.company_documents_in_help_center !== false },
      { key: 'support_tickets', label: 'Support Tickets', icon: 'fa-life-ring', route: 'help_center',
        query: () => ({ tab: 'support' }) },
    ],
  },
  {
    key: 'help_admin',
    label: 'Admin',
    gate: ctx => ctx.isEffectiveAdmin,
    items: [
      { key: 'feedback_review', label: 'Feedback Review', icon: 'fa-comments-o', route: 'help_center',
        query: () => ({ tab: 'feedback' }) },
      { key: 'assistant_insights', label: 'Assistant Insights', icon: 'fa-lightbulb-o', route: 'help_center',
        query: () => ({ tab: 'insights' }) },
      { key: 'help_announcements', label: 'Announcements', icon: 'fa-bullhorn', route: 'help_center',
        query: () => ({ tab: 'announcements' }) },
    ],
  },
  {
    key: 'legal',
    items: [
      { key: 'terms', label: 'Terms of Service', icon: 'fa-gavel', external: true,
        href: () => LEGAL_URLS.terms },
      { key: 'privacy', label: 'Privacy Policy', icon: 'fa-lock', external: true,
        href: () => LEGAL_URLS.privacy },
    ],
  },
]

// ---------------------------------------------------------------------------
// resolution
// ---------------------------------------------------------------------------

function resolveValue(v, ctx) {
  return typeof v === 'function' ? v(ctx) : v
}

function resolveItem(item, ctx) {
  if (item.gate && !item.gate(ctx)) return null
  const resolved = {
    key: item.key,
    label: item.label,
    icon: item.icon || null,
    desc: item.desc || null,
    external: !!item.external,
    placement: item.placement || 'rail',
  }
  if (item.action) {
    // no destination: the rail renders a button that emits this on $root
    resolved.action = item.action
  } else if (item.href) {
    resolved.href = resolveValue(item.href, ctx)
  } else {
    resolved.to = { name: resolveValue(item.route, ctx) }
    const query = resolveValue(item.query, ctx)
    if (query) resolved.to.query = query
    if (item.loose) resolved.loose = true
  }
  return resolved
}

function resolveGroup(group, ctx) {
  if (group.gate && !group.gate(ctx)) return null
  const items = group.items.map(i => resolveItem(i, ctx)).filter(Boolean)
  if (!items.length) return null
  return { key: group.key, label: group.label || null, items }
}

function resolveSection(section, ctx) {
  if (section.gate && !section.gate(ctx)) return null
  const groups = section.groups.map(g => resolveGroup(g, ctx)).filter(Boolean)
  let landing = section.landingRoute ? { name: resolveValue(section.landingRoute, ctx) } : null
  if (landing && section.landingQuery) landing.query = resolveValue(section.landingQuery, ctx)
  if (!landing) {
    const first = groups.length && groups[0].items.find(i => i.to && i.placement === 'rail')
    landing = first ? first.to : null
  }
  // a section with no landing and no content has nothing to show
  if (!landing && !groups.length) return null
  // sections without a fixed landing exist only through their items
  if (!section.landingRoute && !groups.some(g => g.items.some(i => i.placement === 'rail'))) return null
  return {
    key: section.key,
    label: section.label,
    icon: section.icon || null,
    desc: section.desc || null,
    surfacing: section.surfacing,
    landing,
    // true when landing is a real section home page (landingRoute) rather
    // than the first-rail-item fallback -- the tree renders an Overview row
    // only for real home pages so fleet doesn't get a duplicate first item
    ownLanding: !!section.landingRoute,
    landingOnly: !!section.landingOnly,
    groups,
  }
}

// the full visible nav tree for a user: gates applied, dynamic routes and
// queries resolved. plain serializable data; components just render it.
export function resolveNav(ctx) {
  return SECTIONS.map(s => resolveSection(s, ctx)).filter(Boolean)
}

export function resolveUtilities(ctx) {
  return UTILITY_ITEMS.map(i => resolveItem(i, ctx)).filter(Boolean)
}

// the gated help menu for the top-bar help icon: [{ key, label?, items }]
export function resolveHelpMenu(ctx) {
  return HELP_MENU_GROUPS.map(g => resolveGroup(g, ctx)).filter(Boolean)
}

// items flagged for the top bar (sos, notifications) regardless of section.
// walks the raw data, not resolveNav: a section dropped from the rail for
// having no rail items (e.g. fleet on a FLEET tenant with only sos) must
// still surface its topbar items.
export function resolveTopBarItems(ctx) {
  const out = []
  for (const section of SECTIONS) {
    if (section.gate && !section.gate(ctx)) continue
    for (const group of section.groups) {
      if (group.gate && !group.gate(ctx)) continue
      for (const item of group.items) {
        if (item.placement !== 'topbar') continue
        const resolved = resolveItem(item, ctx)
        if (resolved) out.push(resolved)
      }
    }
  }
  return out.concat(resolveUtilities(ctx).filter(i => i.placement === 'topbar'))
}

// gate for the fleet assistant launcher (floating pill in App.vue, bar
// button in fi_top_nav): off on the full-page FA route, honors the tenant
// feature flag (explicit false only -- missing means enabled), the role
// allowlist / per-user access grant, and the hide-launcher preference.
// takes the raw user (not ctx): App.vue calls this before buildNavContext.
export function faLauncherAvailable(user, routeName) {
  if (routeName === 'fleet_assistant') return false
  if (!user || !user.features || !user.features[user.tenant]) return false
  if (user.features[user.tenant].fleet_assistant_enabled === false) return false
  const roleOk = user.role === 'admin' || user.role === 'customer' ||
    user.role === 'customer-admin' || user.role === 'tbn-admin' ||
    user.role === 'manager' || user.role === 'dispatch' || user.fleet_assistant_access
  if (!roleOk) return false
  return !(user.preferences && user.preferences.hide_fleet_assistant_launcher)
}

// active-section lookup for the top nav highlight: route name -> section key.
// covers landings and rail items; callers fall back to path-prefix matching
// for deep/detail routes that never appear in the nav.
export function sectionKeyForRoute(ctx, routeName) {
  if (!routeName) return null
  for (const section of resolveNav(ctx)) {
    if (section.landing && section.landing.name === routeName) return section.key
    for (const group of section.groups) {
      if (group.items.some(i => i.to && i.to.name === routeName)) return section.key
    }
  }
  return null
}
